import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { finalize, map } from 'rxjs/operators';

import { isNilOrEmpty } from 'helpers';
import { AddressFormModel } from 'shared/forms/address-form/address-form.component';

import {
  AddressType,
  CreateAddressesInput, CreateAddressGQL, FieldStatus, FieldStatusInput,
  GetCampaignDefaultCountryGQL, UpdateAddressGQL, UpdateAddressInput
} from 'generated/graphql';
import { Store } from '@ngrx/store';
import { REFETCH_ENTITY_ADDRESS } from 'app/_state/entity/address-refetch.actions';

/**
 * Format of data for the dialog
 */
export interface AddOrUpdateAddressDialogData {
  campaignId: string;
  entityId: string;
  address?: {
    id: string;
    status: any;
    zip: string;
    state: string;
    city: string;
    country: string;
    streetAddress: string;
    addressType: AddressType;
  };
}

@Component({
  selector: 'app-add-or-update-address-dialog',
  templateUrl: './add-or-update-address.dialog.html'
})
export class AddOrUpdateAddressDialog implements OnInit {
  isSaving = false;

  formControl: UntypedFormControl;
  /** If no address data is provided, it is a create action */
  get isCreate() {
    return isNilOrEmpty(this.data?.address?.id);
  }

  static open(dialog: MatDialog, config: MatDialogConfig<AddOrUpdateAddressDialogData>) {
    return dialog.open(AddOrUpdateAddressDialog, config);
  }

  constructor(
    private _getCampaignDefaultCountryGQL: GetCampaignDefaultCountryGQL,
    private _createAddressGQL: CreateAddressGQL,
    private _updateAddressGQL: UpdateAddressGQL,
    private _store: Store,
    public dialogRef: MatDialogRef<AddOrUpdateAddressDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AddOrUpdateAddressDialogData
  ) { }

  ngOnInit(): void {
    if (isNilOrEmpty(this.data?.entityId) || isNilOrEmpty(this.data?.campaignId)) {
      throw new Error('An entityId and campaignId must be passed as data to `AddOrUpdateAddressDialog`');
    }

    this._getDefaultCountryAndBuildForm();
  }

  /**
   * must be called on init
   */
  private _getDefaultCountryAndBuildForm() {
    this._getCampaignDefaultCountryGQL
      .fetch({ campaignId: this.data.campaignId })
      .pipe(
        map(({ data }) => data.getCampaign.defaultCountry)
      ).subscribe((country) => {
        const initialFormData = this._translateToFormData(
          this.data,
          country
        );

        this.formControl = new UntypedFormControl(initialFormData);

        // this.formControl.patchValue(initialFormData);
      });
  }

  onSaveClicked() {
    if (this.isSaving) {
      return;
    }

    this._save$().pipe(
      finalize(() => this.isSaving = false)
    ).subscribe(
      (res) => {
        if (res) {
          const addresses = res.addresses;

          // get the must recent address by updatedAt
          const address = addresses.sort((a, b) => {
            const aDate = new Date(a.updatedAt);
            const bDate = new Date(b.updatedAt);
            return aDate > bDate ? -1 : 1;
          })[0];

          this._store.dispatch(
            REFETCH_ENTITY_ADDRESS({
              payload: {
                addressId: address.id,
                campaignId: this.data.campaignId,
                entityId: this.data.entityId,
                delay: 4000
              }
            })
          );

          this.close(res);
        }
        this.close(res);
      }
    );
  }

  close(data: any = null) {
    this.dialogRef.close(data);
  }

  private _save$() {
    this.isSaving = true;
    return this.isCreate ? this._createAddress() : this._updateAddress();
  }

  _createAddress() {
    const formValue = this.formControl.value;

    const input: CreateAddressesInput = {
      entityId: this.data.entityId,
      campaignId: this.data.campaignId,
      addresses: [
        {
          streetAddress: formValue.streetAddress,
          city: formValue.city,
          state: formValue.state,
          country: formValue.country,
          zip: formValue.zip,
          status: formValue.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
          addressType: formValue.addressType
        }
      ]
    };

    return this._createAddressGQL.mutate({ input }).pipe(
      map((response) => response?.data?.createAddresses.entity)
    );
  }

  _updateAddress() {
    const formValue = this.formControl.value;

    const input: UpdateAddressInput = {
      entityId: this.data.entityId,
      campaignId: this.data.campaignId,
      addressId: this.data.address.id,
      changes: {
        streetAddress: formValue.streetAddress,
        city: formValue.city,
        state: formValue.state,
        country: formValue.country,
        zip: formValue.zip,
        addressType: formValue.addressType,
        status: formValue.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
      }
    };

    return this._updateAddressGQL.mutate({ input }).pipe(
      map((response) => response?.data?.updateAddress.entity)
    );
  }

  private _translateToFormData(dialogData: AddOrUpdateAddressDialogData, defaultCountry: string): AddressFormModel {
    const address = dialogData?.address;

    if (!address) {
      return {
        country: defaultCountry,
        zip: '',
        state: '',
        city: '',
        streetAddress: '',
        addressType: AddressType.Physical
      };
    }

    return {
      verified: address.status === FieldStatus.Verified,
      zip: address.zip,
      state: address.state,
      city: address.city,
      country: address.country,
      streetAddress: address.streetAddress,
      addressType: address.addressType
    };
  }

}
